import { useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    ClickAwayListener,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'views/components/ui-component/cards/MainCard';
import Transitions from 'views/components/ui-component/extended/Transitions';

// assets
import { IconLogout, IconSettings, IconUserCircle } from '@tabler/icons-react';
import { SignOutAction } from '../../../../../../store/action/authAction';
import { logout } from '../../../../../../api/post/Auth';
import { UpdateUserInfoAction } from '../../../../../../store/action/userInfoAction';
import { RESPONSE_CODE, responseResult } from '../../../../../../metadata/constant';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const account = useSelector((state) => state.accountReducer);
    const userInfo = useSelector((state) => state.userInfoReducer);
    const dispatch = useDispatch();
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const navigate = useNavigate();

    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    const handleLogout = async () => {
        try {
            const response = await logout({ refresh_token: account.refreshToken });
            if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                response.json().then((data) => {
                    dispatch(SignOutAction(data));
                    dispatch(UpdateUserInfoAction({}));
                    navigate('/auth/login');
                });
            } else {
                dispatch(SignOutAction('out'));
                dispatch(UpdateUserInfoAction({}));
                navigate('/auth/login');
            }
        } catch (error) {
            dispatch(SignOutAction('out'));
            dispatch(UpdateUserInfoAction({}));
            navigate('/auth/login');
        }
    };

    const handleMyInfo = async (event) => {
        handleClose(event);
        navigate('/auth/me');
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Box sx={{ ...theme.typography.profileBox, display: 'inline-flex', alignItems: 'center' }}>
                <Typography variant="h5" component="div">
                    {userInfo.userInfo.full_name} 님
                </Typography>
                <IconButton
                    sx={{ bgcolor: theme.options.colors.bgdHighlight, ml: '20px' }}
                    onClick={handleToggle}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    ref={anchorRef}
                >
                    <IconUserCircle stroke={2.0} size="1.5rem" color="#ffffff" />
                </IconButton>
                <Popper
                    placement="bottom-end"
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    transition
                    disablePortal
                    popperOptions={{
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 14]
                                }
                            }
                        ]
                    }}
                >
                    {({ TransitionProps }) => (
                        <Transitions in={open} {...TransitionProps}>
                            <Paper>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                            <Box sx={{ p: 2 }}>
                                                <List
                                                    component="nav"
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 350,
                                                        minWidth: 300,
                                                        backgroundColor: theme.palette.background.paper,
                                                        borderRadius: '10px',
                                                        [theme.breakpoints.down('md')]: {
                                                            minWidth: '100%'
                                                        },
                                                        '& .MuiListItemButton-root': {
                                                            mt: 0.5
                                                        }
                                                    }}
                                                >
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                        selected={selectedIndex === 4}
                                                        onClick={handleMyInfo}
                                                    >
                                                        <ListItemIcon>
                                                            <IconUserCircle stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={<Typography variant="body2">내 정보 보기</Typography>} />
                                                    </ListItemButton>
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                        selected={selectedIndex === 4}
                                                        onClick={handleLogout}
                                                    >
                                                        <ListItemIcon>
                                                            <IconLogout stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={<Typography variant="body2">로그아웃</Typography>} />
                                                    </ListItemButton>
                                                </List>
                                            </Box>
                                        </PerfectScrollbar>
                                    </MainCard>
                                </ClickAwayListener>
                            </Paper>
                        </Transitions>
                    )}
                </Popper>
            </Box>
        </>
    );
};

export default ProfileSection;
